import React, { useState, useEffect } from 'react';
import { Label, Form, Input, Button } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import lmsLogo from '../assets/images/lmsLogo.png';
import { loginStudent } from '../helper/student_helper';
import Loader from '../component/Loader/Loader';
import loginBg from '../assets/images/LoginBg.jpg';

const Login = () => {
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [cred, setCred] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // Stronger autofill prevention
  useEffect(() => {
    // Add multiple fake inputs to completely confuse autofill
    const addFakeFields = () => {
      const types = ['text', 'password', 'email'];
      types.forEach((type, i) => {
        const fake = document.createElement('input');
        fake.type = type;
        fake.style.position = 'absolute';
        fake.style.left = '-9999px';
        fake.style.top = `${i * 10}px`;
        fake.autocomplete = 'new-password';
        fake.name = `fake-field-${Date.now()}-${i}`;
        document.body.appendChild(fake);
      });
      
      // Also add a fake form
      const fakeForm = document.createElement('form');
      fakeForm.style.position = 'absolute';
      fakeForm.style.left = '-9999px';
      fakeForm.innerHTML = `
        <input type="text" name="username" value="">
        <input type="password" name="password" value="">
        <input type="email" name="email" value="">
      `;
      fakeForm.autocomplete = 'off';
      document.body.appendChild(fakeForm);
    };

    addFakeFields();
    
    // Cleanup
    return () => {
      const fakeElements = document.querySelectorAll('input[style*="-9999px"], form[style*="-9999px"]');
      fakeElements.forEach(el => el.remove());
    };
  }, []);

  const validate = () => {
    const e = {};
    if (!cred.email?.trim()) e.email = "Email or mobile is required";
    if (!cred.password) e.password = "Password is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    try {
      if (!validate()) return;
      setLoader(true);
      const data = await loginStudent(cred);
      if (data?.success) {
        toast.success(data?.message || "Logged in");
        localStorage.setItem("token", data?.token);
        localStorage.setItem("student", JSON.stringify(data?.studentDto));
        navigate("/");
      } else {
        toast.error(data?.message || "Login failed");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const onChange = (key, value) => {
    setCred(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      {loader && <Loader />}

      <style>{`
/* Full-bleed page - no side gutters */
.login-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  background: #f6f9fc;
  overflow-x: hidden;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  margin: 0;
  padding: 0;
}

/* wrapper is full-bleed and fills viewport */
.login-wrap {
  width: 100%;
  margin: 0;
  border-radius: 0;
  overflow: hidden;
  display: flex;
  position: relative;
  background: transparent;
  min-height: 100vh;
}

/* decorative background behind everything (large screens) */
.login-wrap .bg-decor {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image: url("${loginBg}");
  background-size: cover;
  background-position: center center;
  opacity: 0.12;
  filter: blur(8px) saturate(0.9);
  transform: scale(1.02);
  pointer-events: none;
}

/* HERO column ~60% */
.hero-col {
  flex: 0 0 60%;
  min-width: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 48px;
  z-index: 2;
  overflow: hidden;
}

/* make the hero image centered and cover the area */
.hero-bg {
  position: absolute;
  inset: 0;
  background-image: url("${loginBg}");
  background-size: cover;
  background-position: center center;
  z-index: 1;
  opacity: 0.95;
  filter: saturate(0.95);
  transform: translateZ(0);
}

/* overlay for hero text */
.hero-overlay {
  position: relative;
  z-index: 2;
  max-width: 720px;
  background: linear-gradient(90deg, rgba(0,0,0,0.46), rgba(0,0,0,0.18));
  border-radius: 12px;
  padding: 36px;
  color: #fff;
  box-shadow: 0 8px 30px rgba(2,6,23,0.18);
  transform: translateY(8px);
  animation: fadeSlideIn 700ms ease both;
}

/* FORM column ~40% and fills its area fully (no visible gutter) */
.form-col {
  flex: 0 0 40%;
  min-width: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 2;
  background: transparent;
}

/* form-card now stretches full width of the column; card has inner padding */
.form-card {
  width: 100%;
  height: 100%;
  display: block;
  max-width: none;
  border-radius: 0;
  background: #fff;
  padding: 40px;
  box-shadow: none;
  box-sizing: border-box;
}

/* layout piece sizes inside card kept comfortable */
.form-inner {
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
}

/* rest of UI */
.form-logo { 
  display:flex; 
  align-items:center; 
  gap:10px; 
  justify-content:center; 
  margin-bottom: 12px; 
}

.form-title { 
  text-align:center; 
  margin-bottom: 4px; 
  font-weight: 800;
  font-size: 1.8rem;
  color: #0b3161;
}

.form-sub { 
  text-align:center; 
  color: #6b7280; 
  margin-bottom: 24px; 
  font-size: 0.95rem; 
}

.form-row { 
  margin-bottom: 16px; 
  position: relative;
}

.input-field { 
  border-radius: 8px; 
  height: 44px; 
  padding: 8px 12px; 
  background-color: #fff !important;
  width: 100%;
  border: 1px solid #ddd;
}

.input-field:focus {
  border-color: #0b3161;
  box-shadow: 0 0 0 2px rgba(11, 49, 97, 0.1);
  outline: none;
}

/* Password toggle button */
.password-toggle-btn {
  position: absolute;
  right: 10px;
  top: 38px;
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle-btn:hover {
  color: #0b3161;
}

.forgot-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  font-size: 14px;
}

.btn-primary { 
  background: linear-gradient(90deg, #0b3161, #1a4a82); 
  border: none; 
  color: #fff;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  width: 100%;
  height: 44px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(11, 49, 97, 0.3);
}

.form-foot { 
  text-align:center; 
  margin-top: 24px; 
  font-size: 14px; 
  color: #9aa4b2; 
}

.error-text {
  color: #d9534f;
  font-size: 13px;
  margin-top: 6px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox-container input[type="checkbox"] {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  cursor: pointer;
}

.checkbox-container label {
  font-size: 14px;
  color: #64748b;
  cursor: pointer;
}

/* Strong autofill prevention */
input[data-autocomplete="off"] {
  background-color: #fff !important;
}

/* Multiple strategies to prevent autofill */
input::-webkit-contacts-auto-fill-button,
input::-webkit-credentials-auto-fill-button {
  visibility: hidden;
  display: none !important;
  pointer-events: none;
  position: absolute;
  right: 0;
}

/* Disable browser suggestions */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px white inset !important;
  box-shadow: 0 0 0 1000px white inset !important;
  -webkit-text-fill-color: #333 !important;
  transition: background-color 5000000s ease-in-out 0s;
}

@keyframes fadeSlideIn { 
  from { opacity:0; transform: translateY(8px); } 
  to { opacity:1; transform: translateY(0); } 
}

/* Mobile: hide hero, form fills screen (full-bleed) */
@media (max-width: 991.98px) {
  .login-wrap { min-height: 100vh; }
  .hero-col { display: none; }
  .form-col {
    flex: 1 1 100%;
    padding: 28px;
    background-image: linear-gradient(rgba(6,10,20,0.46), rgba(6,10,20,0.36)), url("${loginBg}");
    background-size: cover;
    background-position: center center;
    min-height: 100vh;
    align-items: flex-start;
    justify-content: flex-start;
  }
  .form-card { 
    width: 92%; 
    margin-top: 36px; 
    padding: 28px; 
    border-radius: 12px; 
    box-shadow: 0 18px 50px rgba(2,6,23,0.16); 
  }
  .login-wrap .bg-decor { display: none; }
}
      `}</style>

      <div className="login-page" role="main" aria-label="Login">
        <div className="login-wrap">
          <div className="bg-decor" aria-hidden="true" />

          {/* HERO (left on desktop) */}
          <div className="hero-col" role="region" aria-label="Hero">
            <div className="hero-bg" aria-hidden="true" />
            <div className="hero-overlay" role="article">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 34, fontWeight: 800, marginBottom: 8 }}>Welcome Back!</div>
                  <div style={{ marginBottom: 16, color: 'rgba(255,255,255,0.92)' }}>
                    Continue your learning journey with practical courses, expert mentors, and real-world projects.
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ background: 'rgba(255,255,255,0.12)', padding: '8px 12px', borderRadius: 999, fontWeight: 600 }}>Live Projects</div>
                    <div style={{ background: 'rgba(255,255,255,0.12)', padding: '8px 12px', borderRadius: 999, fontWeight: 600 }}>Mentor Support</div>
                    <div style={{ background: 'rgba(255,255,255,0.12)', padding: '8px 12px', borderRadius: 999, fontWeight: 600 }}>Placement Assistance</div>
                    <div style={{ background: 'rgba(255,255,255,0.12)', padding: '8px 12px', borderRadius: 999, fontWeight: 600 }}>Certificate</div>
                  </div>

                  <div style={{ marginTop: 16, color: 'rgba(255,255,255,0.9)' }}>
                    <small>Thousands of learners are transforming their careers. Join them today.</small>
                  </div>
                </div>

                <div style={{ width: 92, height: 92, borderRadius: 12, background: 'linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.06))', transform: 'rotate(12deg)' }} aria-hidden="true" />
              </div>
            </div>
          </div>

          {/* FORM (right on desktop) */}
          <div className="form-col" role="region" aria-label="Login form">
            <div className="form-card" role="form" aria-labelledby="login-heading">
              <div className="form-inner">
                <div className="form-logo">
                  <Link to="/" aria-label="home">
                    <img src={lmsLogo} alt="logo" height="44" />
                  </Link>
                </div>

                <h3 id="login-heading" className="form-title">Welcome Back</h3>
                <div className="form-sub">Sign in to continue to your dashboard</div>

                <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                  <div className="form-row">
                    <Label htmlFor="email">Email or Mobile</Label>
                    <Input 
                      id="email" 
                      name="user_login" 
                      value={cred.email} 
                      onChange={e => onChange('email', e.target.value)} 
                      className="input-field" 
                      placeholder="Enter email or mobile" 
                      type="text"
                      data-autocomplete="off"
                      readOnly
                      onFocus={(e) => e.target.removeAttribute('readonly')}
                    />
                    {errors.email && <div className="error-text">{errors.email}</div>}
                  </div>

                  <div className="form-row">
                    <Label htmlFor="password">Password</Label>
                    <div style={{ position: 'relative' }}>
                      <Input 
                        id="password" 
                        name="user_pass" 
                        value={cred.password} 
                        onChange={e => onChange('password', e.target.value)} 
                        className="input-field" 
                        type={showPassword ? 'text' : 'password'} 
                        placeholder="Enter your password"
                        autoComplete="new-password"
                        data-autocomplete="off"
                        readOnly
                        onFocus={(e) => e.target.removeAttribute('readonly')}
                      />
                      <button 
                        type="button" 
                        className="password-toggle-btn" 
                        aria-label={showPassword ? "Hide password" : "Show password"} 
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                    {errors.password && <div className="error-text">{errors.password}</div>}
                  </div>

                  <div className="forgot-row">
                    <div className="checkbox-container">
                      <Input 
                        type="checkbox" 
                        id="remember" 
                        autoComplete="off"
                        name="remember"
                        data-autocomplete="off"
                      />
                      <Label htmlFor="remember">Remember me</Label>
                    </div>
                    <div>
                      <Link to="/forgot-password" style={{ color: '#0b3161', fontWeight: 500, textDecoration: 'none' }}>
                        Forgot Password?
                      </Link>
                    </div>
                  </div>

                  <div className="form-row">
                    <Button 
                      color="primary" 
                      className="btn-primary" 
                      onClick={handleSubmit} 
                      disabled={loader}
                      type="submit"
                    >
                      {loader ? 'Signing in...' : 'Log In'}
                    </Button>
                  </div>

                  <div className="form-foot">
                    <small>Don't have an account? <Link to="/signup">Sign Up</Link></small>
                  </div>
                  
                  <div style={{ textAlign: 'center', marginTop: 20, color: '#c1c9d6', fontSize: 12 }}>
                    © {new Date().getFullYear()} SS — Created By Amass Skill Ventures
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;